import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { copilotConfig } from '../../../config/copilotConfig';
import {
  fetchAiUsage,
  streamAiChat,
  getDecisionApiError
} from '../../../services/decisionApi';

const QUICK_PROMPTS = [
  '我的持仓里有哪些产品？',
  '持仓里有谁需要关注或减仓？',
  '最近这个产品表现怎么样？',
  '这个产品综合评分如何？适合持有吗？',
  '当前宏观环境对固收类产品有什么影响？'
];

function CopilotAvatar({ size = 'md', className = '' }) {
  const cls = size === 'sm' ? 'decision-copilot-avatar-sm' : 'decision-copilot-avatar-md';
  return (
    <span className={`decision-copilot-avatar ${cls} ${className}`.trim()}>
      <img src={copilotConfig.avatarUrl} alt={copilotConfig.name} />
    </span>
  );
}

export default function CopilotPanel({ productId: productIdProp, productName: productNameProp }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routeProductId = location.pathname === '/product' ? searchParams.get('id') : '';
  const productId = productIdProp || routeProductId || '';
  const productName = productNameProp || '';

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [usage, setUsage] = useState(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);

  const refreshUsage = useCallback(() => {
    fetchAiUsage()
      .then((data) => {
        if (data?.ok) setUsage(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (open) refreshUsage();
  }, [open, refreshUsage]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (panelRef.current?.contains(event.target)) return;
      if (event.target.closest('.decision-copilot-fab')) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  async function sendMessage(text) {
    const message = String(text || input || '').trim();
    if (!message || loading) return;
    setError('');
    setInput('');
    setLoading(true);
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      await streamAiChat(
        {
          message,
          sessionId,
          productId: productId || undefined,
          stream: true
        },
        (chunk) => {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') {
              next[next.length - 1] = { ...last, content: (last.content || '') + chunk };
            }
            return next;
          });
        },
        (done) => {
          if (done.sessionId) setSessionId(done.sessionId);
          if (done.content) {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last?.role === 'assistant') {
                next[next.length - 1] = { ...last, content: done.content };
              }
              return next;
            });
          }
          refreshUsage();
        }
      );
    } catch (err) {
      setError(getDecisionApiError(err, 'AI 对话失败'));
      setMessages((prev) => prev.slice(0, -2));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`decision-copilot ${open ? 'open' : ''}`}>
      {open ? (
        <div className="decision-copilot-panel" ref={panelRef}>
          <div className="decision-copilot-header">
            <div className="decision-copilot-header-main">
              <CopilotAvatar size="sm" />
              <div>
                <strong>{copilotConfig.title}</strong>
                <p className="decision-muted decision-copilot-subtitle">
                  {productName
                    ? `上下文：${productName}`
                    : productId
                      ? `上下文产品 ID：${productId}`
                      : '可在产品分析页自动带入当前产品'}
                </p>
              </div>
            </div>
            <div className="decision-copilot-header-actions">
              {usage ? (
                <span className="decision-copilot-usage">
                  今日 {usage.used}/{usage.limit}
                </span>
              ) : null}
              <button
                type="button"
                className="decision-copilot-close"
                aria-label="关闭"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
          </div>

          <div className="decision-copilot-chips">
            {QUICK_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="decision-copilot-chip"
                disabled={loading}
                onClick={() => sendMessage(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>

          <div className="decision-copilot-messages" ref={listRef}>
            {!messages.length ? (
              <div className="decision-copilot-welcome">
                <CopilotAvatar />
                <p>你好，我是{copilotConfig.name}。自由提问，回答基于工具查库，不构成投资建议。</p>
              </div>
            ) : null}
            {messages.map((msg, idx) => (
              <div
                key={`${idx}-${msg.role}`}
                className={`decision-copilot-row decision-copilot-row-${msg.role}`}
              >
                {msg.role === 'assistant' ? <CopilotAvatar size="sm" /> : null}
                <div className={`decision-copilot-msg decision-copilot-msg-${msg.role}`}>
                  {msg.role === 'user' ? <div className="decision-copilot-msg-role">你</div> : null}
                  <div className="decision-copilot-msg-body">
                    {msg.content || (loading && idx === messages.length - 1 ? '思考中…' : '')}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {error ? <p className="decision-error" style={{ margin: '8px 0 0' }}>{error}</p> : null}

          <div className="decision-copilot-input-row">
            <textarea
              className="decision-copilot-input"
              rows={2}
              placeholder={`向 ${copilotConfig.name} 提问…`}
              value={input}
              disabled={loading}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button
              type="button"
              className="decision-btn"
              disabled={loading || !input.trim()}
              onClick={() => sendMessage()}
            >
              {loading ? '…' : '发送'}
            </button>
          </div>
          <p className="auxiliary-note" style={{ marginTop: 8 }}>辅助参考，不构成投资建议</p>
        </div>
      ) : null}

      <button
        type="button"
        className="decision-copilot-fab"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? '收起 Copilot' : `打开 ${copilotConfig.name}`}
        title={copilotConfig.name}
      >
        <span className="decision-copilot-fab-ring" aria-hidden="true" />
        <CopilotAvatar size="md" className="decision-copilot-fab-avatar" />
        {!open ? <span className="decision-copilot-fab-badge">AI</span> : null}
      </button>
    </div>
  );
}
